import React from "react";
import { InputNumber, Select, Switch } from "antd";
import {
  NUMATO_DEVICE_BRANCHES,
  NUMATO_MESSAGE_OPTION_GROUPS,
  type NumatoFlatMessage,
  compositeKeyFromNumatoFlatMessage,
  normalizeNumatoFlatMessage,
  numatoFlatMessageCarriesPosition,
  numatoFlatMessageFromCompositeKey,
  numatoFlatMessageLabel,
} from "../../../flatbuffers/types/numatoFlatMessage";

/** uint16 — clampIntToUint16 in LiftCloud's expander truncates above this. */
const MAX_POSITION = 65535;

const hintStyle: React.CSSProperties = { fontSize: 10, color: "#d89614", display: "block" };

export interface NumatoFlatMessageFieldsProps {
  msg: NumatoFlatMessage;
  onChange: (updated: NumatoFlatMessage) => void;
}

/**
 * The LN2 message half of one Numato mapping row: message, FlatBuffer device
 * branch, position (only where the branch has the slot) and active.
 *
 * The picker is keyed on the full composite key "<category>:<variant>:<value>",
 * so the enum value the operator chose is what gets persisted. The previous
 * control was keyed on the bare category, which collapsed every choice to the
 * same placeholder.
 */
export default function NumatoFlatMessageFields({ msg, onChange }: NumatoFlatMessageFieldsProps) {
  const selectedKey = compositeKeyFromNumatoFlatMessage(msg);
  const showPosition = numatoFlatMessageCarriesPosition(msg);

  return (
    <div className="row">
      <div className="col-12 col-md-5 mt-2">
        <span className="generalStyles__inputLabel">LN2 Message</span>
        <Select
          className="generalStyles__width100"
          size="small"
          showSearch
          // Filter and closed-state display both use the Option's `label`
          // ("Status: Power") while the dropdown renders the bare option name
          // under its category group. The previous control filtered on
          // `children`, which was only ever a category name.
          optionFilterProp="label"
          optionLabelProp="label"
          placeholder="Select an LN2 message"
          value={selectedKey ?? undefined}
          onChange={(val: string) => {
            const next = numatoFlatMessageFromCompositeKey(val, msg);
            if (next) onChange(next);
          }}
          style={{ marginTop: 4 }}
        >
          {NUMATO_MESSAGE_OPTION_GROUPS.map((group) => (
            <Select.OptGroup key={group.categoryKey} label={group.label}>
              {group.options.map((opt) => (
                <Select.Option
                  key={opt.key}
                  value={opt.key}
                  label={opt.displayName}
                  title={opt.displayName}
                >
                  {opt.label}
                </Select.Option>
              ))}
            </Select.OptGroup>
          ))}
        </Select>
        {selectedKey === null && <span style={hintStyle}>{numatoFlatMessageLabel(msg)}</span>}
      </div>

      <div className="col-12 col-md-3 mt-2">
        <span className="generalStyles__inputLabel">FlatBuffer Device Branch</span>
        <Select
          className="generalStyles__width100"
          size="small"
          value={msg.fbs_device_type}
          onChange={(val: number) =>
            onChange(normalizeNumatoFlatMessage({ ...msg, fbs_device_type: val }))
          }
          style={{ marginTop: 4 }}
        >
          {NUMATO_DEVICE_BRANCHES.map((opt) => (
            <Select.Option key={opt.value} value={opt.value}>
              {opt.label}
            </Select.Option>
          ))}
        </Select>
        <span style={{ fontSize: 10, color: "#888", display: "block" }}>
          Elevator vs escalator schema branch — not the same as Device Type.
        </span>
      </div>

      {showPosition && (
        <div className="col-12 col-md-2 mt-2">
          <span className="generalStyles__inputLabel">Position</span>
          <InputNumber
            className="generalStyles__width100"
            size="small"
            min={0}
            max={MAX_POSITION}
            value={msg.position ?? 0}
            onChange={(v) => onChange({ ...msg, position: v ?? 0 })}
            style={{ marginTop: 4 }}
          />
        </div>
      )}

      <div className="col-12 col-md-2 mt-2">
        <span className="generalStyles__inputLabel">Active</span>
        <div style={{ marginTop: 8 }}>
          <Switch
            checked={msg.active}
            onChange={(checked) => onChange({ ...msg, active: checked })}
          />
        </div>
      </div>
    </div>
  );
}
